import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts, getCategories, BlogPost } from '../data/blog';

const BlogList: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>(blogPosts);
  const categories = ['All', ...getCategories()];

  useEffect(() => {
    let posts = blogPosts;

    // Filter by category
    if (selectedCategory !== 'All') {
      posts = posts.filter(post => post.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      posts = posts.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    setFilteredPosts(posts);
  }, [selectedCategory, searchQuery]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <>
      {/* Hero Section */}
      <section className="wiziai-blog-hero">
        <div className="wiziai-hero-gradient"></div>
        <div className="container">
          <div className="row justify-content-center align-items-center py-5">
            <div className="col-lg-10 text-center">
              <div className="wiziai-hero-content">
                <h1 className="wiziai-hero-title mb-3 animate__animated animate__fadeInUp">
                  WiZiAi <span className="wiziai-gradient-text-yellow">Learning Hub</span>
                </h1>
                <p className="wiziai-hero-subtitle mb-4 animate__animated animate__fadeInUp animate__delay-1s">
                  Expert insights, study strategies, and tips to ace your JEE & NEET preparation
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content Section */}
      <div className="container-fluid py-5 bg-light">
        <div className="container py-5">
          {/* Search and Filter Section */}
          <div className="row mb-5">
            <div className="col-lg-8 mx-auto">
              {/* Search Bar */}
              <div className="search-bar mb-4">
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0">
                    <i className="fas fa-search text-primary"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0 ps-0"
                    placeholder="Search articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="category-filter d-flex flex-wrap gap-2 justify-content-center">
                {categories.map((category) => (
                  <button
                    key={category}
                    className={`btn ${selectedCategory === category ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Blog Posts Grid */}
          {filteredPosts.length > 0 ? (
            <div className="row g-4">
              {filteredPosts.map((post, index) => (
                <div 
                  key={post.id} 
                  className="col-lg-4 col-md-6 wow fadeInUp" 
                  data-wow-delay={`${0.1 + index * 0.1}s`}
                >
                  <div className="blog-card card h-100 border-0 shadow-sm">
                    {/* Featured Image */}
                    <div className="blog-card-image position-relative overflow-hidden">
                      <img 
                        src={post.featuredImage} 
                        alt={post.title}
                        className="card-img-top"
                      />
                      <div className="blog-category-badge position-absolute top-0 start-0 m-3">
                        <span className="badge bg-primary">{post.category}</span>
                      </div>
                    </div>

                    <div className="card-body d-flex flex-column">
                      {/* Meta Info */}
                      <div className="blog-meta d-flex align-items-center mb-3 text-muted small">
                        <span className="me-3">
                          <i className="far fa-calendar-alt me-1"></i>
                          {formatDate(post.publishedDate)}
                        </span>
                        <span className="me-3">
                          <i className="far fa-clock me-1"></i>
                          {post.readTime}
                        </span>
                        {post.views && (
                          <span>
                            <i className="far fa-eye me-1"></i>
                            {post.views}
                          </span>
                        )}
                      </div>

                      {/* Title */}
                      <h5 className="card-title fw-bold mb-3">
                        <Link 
                          to={`/blog/${post.slug}`} 
                          className="text-dark text-decoration-none hover-primary"
                        >
                          {post.title}
                        </Link>
                      </h5>

                      {/* Excerpt */}
                      <p className="card-text text-muted mb-3 flex-grow-1">
                        {post.excerpt}
                      </p>

                      {/* Tags */}
                      <div className="blog-tags mb-3">
                        {post.tags.slice(0, 3).map((tag, idx) => (
                          <span key={idx} className="badge bg-light text-dark me-1 mb-1">
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* Author & Read More */}
                      <div className="d-flex justify-content-between align-items-center mt-auto pt-3 border-top">
                        <div className="blog-author d-flex align-items-center">
                          <div className="author-avatar me-2">
                            <i className="fas fa-user-circle fa-2x text-primary"></i>
                          </div>
                          <div>
                            <div className="author-name small fw-bold">{post.author.name}</div>
                            <div className="author-role small text-muted">{post.author.role}</div>
                          </div>
                        </div>
                        <Link 
                          to={`/blog/${post.slug}`}
                          className="btn btn-outline-primary btn-sm"
                        >
                          Read More
                          <i className="fas fa-arrow-right ms-1"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="row">
              <div className="col-12 text-center py-5">
                <i className="fas fa-search fa-4x text-muted mb-3"></i>
                <h4 className="text-muted">No articles found</h4>
                <p className="text-muted">Try adjusting your search or filter criteria</p>
              </div>
            </div>
          )}

          {/* Call to Action */}
          <div className="row mt-5">
            <div className="col-lg-10 mx-auto">
              <div className="cta-card card border-0 shadow-lg overflow-hidden">
                <div className="card-body p-5 text-center position-relative" 
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white'
                  }}>
                  <h3 className="mb-3">Ready to Start Your Learning Journey?</h3>
                  <p className="mb-4 fs-5">
                    Join thousands of students who are already preparing smarter with WiZiAi
                  </p>
                  <Link 
                    to="/signup" 
                    className="btn wiziai-btn-primary btn-lg px-5"
                  >
                    Start Free Trial
                    <i className="fas fa-arrow-right ms-2"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogList;
